import { useApolloClient } from "@apollo/client";
import { useCallback, useEffect } from "react";
import { useRouter } from "solito/router";
import { useShallow } from "zustand/react/shallow";
import { eventTracking__LinkAttemptStatus } from "@medbillai/generated-graphql-types";
import { Headline1, YStack } from "@medbillai/ui";
import { executionIdFromWorkflowId } from "@medbillai/utils/index";
import { ScreenView } from "../../components/ScreenView";
import { useAccountLinkDataStore } from "../../lib/accounts-link/accountLinkStore";
import { useAccountLinkAnalytics } from "../../lib/accounts-link/useAccountLinkAnalytics";
import { useAccountLinkNavigation } from "../../lib/accounts-link/utils";
import { useEventTracking } from "../../lib/event-tracking/eventTracking";
import { SingularEvents, singularService } from "../../lib/singular";
import { AccountLogo } from "./components/AccountLogo";
import { LinkingProgressLayout } from "./components/LinkingProgressLayout";
import { StatusIndicator } from "./components/linking-progress/StatusIndicator";
import { FakeDoorMmfaFlow } from "./fake-door-mmfa/FakeDoorMmfaFlow";
import { useFakeDoorMmfaVariant } from "./fake-door-mmfa/useFakeDoorMmfaVariant";
import { linksQuery } from "./utils/queries";
const DEFAULT_PAYER_NAME = "your provider";
const SUCCESS_AUTO_ADVANCE_MS = 3000;
export const SuccessContent = ({
  insuranceSlug
}: {
  insuranceSlug?: string;
}) => <YStack f={1} jc="center" ai="center" pb="$6" gap="$8" data-sentry-element="YStack" data-sentry-component="SuccessContent" data-sentry-source-file="success.tsx">
    <AccountLogo orgSlug={insuranceSlug} style={{
    borderRadius: "$12",
    borderColor: "$strokeSecondary",
    backgroundColor: "$background",
    borderWidth: "$0.5",
    justifyContent: "center",
    alignItems: "center",
    height: 70,
    width: 70,
    zIndex: 2
  }} imageStyle={{
    scale: insuranceSlug ? 1.2 : 1.6
  }} data-sentry-element="AccountLogo" data-sentry-source-file="success.tsx" />

    <Headline1 marginHorizontal="$8" textAlign="center" data-sentry-element="Headline1" data-sentry-source-file="success.tsx">
      Your account is now linked!
    </Headline1>
  </YStack>;
export function SuccessScreen() {
  const {
    push
  } = useRouter();
  const client = useApolloClient();
  const navigation = useAccountLinkNavigation();
  const {
    trackEvent
  } = useAccountLinkAnalytics();
  const mmfaVariant = useFakeDoorMmfaVariant();
  const isMmfaActive = mmfaVariant !== null;
  const selectedAccountLinkEndpoint = useAccountLinkDataStore(useShallow(state => state.selectedAccountLinkEndpoint));
  const workflowId = useAccountLinkDataStore(state => state.workflowId);
  const insuranceTracker = useEventTracking();

  // This whole logic is very convoluted and needs to be refactored.
  // TODO: https://linear.app/medbillai/issue/ENG-5672/refactor-exit-logic-between-onboarding-and-standard-flows-from-success
  const onClickSuccess = useCallback(() => {
    push(navigation.webAgent.notifications);
  }, [navigation, push]);
  const onMmfaExit = useCallback(() => {
    push(navigation.exit);
  }, [navigation.exit, push]);
  useEffect(() => {
    // Refetch the query so the home page gets updated before user returns
    void client.refetchQueries({
      include: [linksQuery]
    });
    const webAgentExecutionId = workflowId && executionIdFromWorkflowId(workflowId);
    void insuranceTracker.trackInsuranceLinkAttempt({
      status: eventTracking__LinkAttemptStatus.COMPLETED,
      baseEndpointId: selectedAccountLinkEndpoint?.id,
      webAgentExecutionId
    });
    singularService.event(SingularEvents.insuranceLinkSuccess);
    trackEvent("FinishedInsuranceLinking", {
      linkType: "WEB_AGENT",
      status: "SUCCESS"
    });

    // When MMFA experiment is active, the flow handles its own navigation.
    // Otherwise, auto-navigate to the next screen after 3 seconds.
    if (!isMmfaActive) {
      const timeout = setTimeout(onClickSuccess, SUCCESS_AUTO_ADVANCE_MS);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [client, insuranceTracker, isMmfaActive, onClickSuccess, selectedAccountLinkEndpoint?.id, trackEvent, workflowId]);
  const payerName = selectedAccountLinkEndpoint?.displayName ?? DEFAULT_PAYER_NAME;

  // When MMFA is active, replace the success screen with the MMFA flow as a
  // full takeover on both platforms. Renders inside the already-presented
  // accounts-link modal stack — no nested modal.
  if (isMmfaActive && mmfaVariant) {
    return <FakeDoorMmfaFlow variant={mmfaVariant} payerName={payerName} onExit={onMmfaExit} />;
  }
  return <ScreenView extraPadding centerHorizontal backgroundRadialGradient scrollViewProps={{
    "$platform-web": {
      height: "100%",
      flex: 1
    }
  }} data-sentry-element="ScreenView" data-sentry-component="SuccessScreen" data-sentry-source-file="success.tsx">
      <YStack f={1} onPress={onClickSuccess} data-sentry-element="YStack" data-sentry-source-file="success.tsx">
        <LinkingProgressLayout topSection={<SuccessContent insuranceSlug={selectedAccountLinkEndpoint?.slug ?? undefined} />} bottomSection={<StatusIndicator state="success" statusText="Connected" />} data-sentry-element="LinkingProgressLayout" data-sentry-source-file="success.tsx" />
      </YStack>
    </ScreenView>;
}